import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Set up axios interceptor to attach token to every request
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const tryRefreshToken = useCallback(async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return false;

    try {
      const response = await axios.post(`${API_URL}/api/auth/refresh`, {
        refresh_token: refreshToken
      });
      if (response.data.token) {
        localStorage.setItem('access_token', response.data.token);
      }
      setUser(response.data);
      return true;
    } catch {
      localStorage.removeItem('refresh_token');
      return false;
    }
  }, []);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('access_token');

    if (token) {
      try {
        const response = await axios.get(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        setUser(response.data);
        setLoading(false);
        return;
      } catch {
        localStorage.removeItem('access_token');
      }
    }

    // Access token missing or expired — try refresh
    const refreshed = await tryRefreshToken();
    if (!refreshed) {
      setUser(null);
    }
    setLoading(false);
  }, [tryRefreshToken]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, 
      { email, password },
      { withCredentials: true }
    );
    if (response.data.token) {
      localStorage.setItem('access_token', response.data.token);
    }
    setUser(response.data);
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API_URL}/api/auth/register`,
      { email, password, name },
      { withCredentials: true }
    );
    if (response.data.token) {
      localStorage.setItem('access_token', response.data.token);
    }
    setUser(response.data);
    return response.data;
  };

  const requestMagicLink = async (email, name = '') => {
    const response = await axios.post(`${API_URL}/api/auth/magic-link/request`,
      { email, name },
      { withCredentials: true }
    );
    return response.data;
  };

  const verifyMagicLink = async (token, rememberDevice = false) => {
    const response = await axios.post(`${API_URL}/api/auth/magic-link/verify`,
      { token, remember_device: rememberDevice },
      { withCredentials: true }
    );
    if (response.data.token) {
      localStorage.setItem('access_token', response.data.token);
    }
    if (response.data.refresh_token) {
      localStorage.setItem('refresh_token', response.data.refresh_token);
    }
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch {
      // ignore
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      logout, 
      checkAuth,
      requestMagicLink,
      verifyMagicLink
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
